import { Component, EventEmitter, Output, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { RouterOutlet } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { MatBadgeModule } from '@angular/material/badge';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { TranslationService } from '../../core/services/translation.service';
import { TranslateModule } from '@ngx-translate/core';
import { AccessControlService } from '../../core/services/access-control.service';
import { NotificationService } from '../../core/services/notifications.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { map, shareReplay, startWith, Subscription, switchMap, tap } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';


@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatBadgeModule,
    CommonModule,
    TranslateModule,
    MatDividerModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent {
  private readonly keycloakService = inject(KeycloakService);
  private translationService = inject(TranslationService);
  private accessControlService = inject(AccessControlService);
  private notificationService = inject(NotificationService);
  private snackBar = inject(MatSnackBar);

  @Output() toggleSidebarEvent = new EventEmitter<void>();
  notifications$ = this.accessControlService.getNotifications();
  unseenCount = 0;
  isConnected = false;

  private connectionSub?: Subscription;
  private notifSub?: Subscription;
  private unseenCountSub?: Subscription;

  ngOnInit() {
    if (environment.agrinio) {
      this.notificationService.startListening();

      this.connectionSub = this.notificationService.connectionStatus$
        .subscribe(status => this.isConnected = status);
      this.notifications$ = this.notificationService.refreshTrigger$.pipe(
        startWith(void 0),
        switchMap(() => this.accessControlService.getNotifications().pipe(shareReplay(1)))
      );

      this.unseenCountSub = this.notifications$.pipe(
        map(res => res.results.filter(n => !n.seen_at).length)
      ).subscribe(count => this.unseenCount = count);

      this.notifSub = this.notificationService.notifications$.subscribe(notification => {
        this.snackBar.open(notification.message, 'Close', { duration: 5000 });
      });
    }
  }

  markAsSeen(uuid: string) {
    this.accessControlService.markNotificationAsSeen(uuid).subscribe({
      next: () => {
        this.notificationService.refreshTrigger.next();
      },
      error: (err) => {
        console.error('Error marking notification as seen:', err);
        this.snackBar.open('Failed to mark notification as seen', 'Close', { duration: 3000 });
      }
    });
  }

  ngOnDestroy() {
    this.connectionSub?.unsubscribe();
    this.notifSub?.unsubscribe();
    this.unseenCountSub?.unsubscribe();
    this.notificationService.stopListening();
  }

  refreshNotifications() {
    this.notifications$ = this.accessControlService.getNotifications().pipe(
      shareReplay(1)
    );

    this.notifications$.pipe(
      map(response => response.results),
      tap(notifications => {
        this.unseenCount = notifications.filter(n => !n.seen_at).length;
      })
    ).subscribe();
  }

  logout() {
    this.keycloakService.logout(window.location.origin);
  }

  toggleSidebar() {
    this.toggleSidebarEvent.emit();
  }

  get environment() {
    return environment;
  }

  changeLanguage(lang: string) {
    this.translationService.changeLang(lang);
  }
}